import React, { useState, useEffect } from 'react';
import { ROUTES } from '@/constants/routes';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '@/store/languageStore';
import { pricelistService, Category, Product } from '@/services/crm/pricelistService';
import SystemPackagesManager from '@/components/crm/SystemPackagesManager';
import { toast } from 'react-hot-toast';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  FolderIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  ClockIcon,
  CalendarIcon,
  TruckIcon,
  UserIcon,
  CurrencyDollarIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline';

// Labels are now resolved via t() inside components

const Pricelist: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const pricingTypeLabels: Record<string, { label: string; icon: React.ReactNode }> = {
    hourly: { label: t('crm.pricingHourly'), icon: <ClockIcon className="w-4 h-4" /> },
    monthly: { label: t('crm.pricingMonthly'), icon: <CalendarIcon className="w-4 h-4" /> },
    per_vehicle: { label: t('crm.pricingPerVehicle'), icon: <TruckIcon className="w-4 h-4" /> },
    per_user: { label: t('crm.pricingPerUser'), icon: <UserIcon className="w-4 h-4" /> },
    one_time: { label: t('crm.pricingOneTime'), icon: <CurrencyDollarIcon className="w-4 h-4" /> },
    custom: { label: t('crm.pricingCustom'), icon: <CurrencyDollarIcon className="w-4 h-4" /> },
  };

  const productTypeLabels: Record<string, string> = {
    service: t('crm.productTypeService'),
    material: t('crm.productTypeMaterial'),
    bundle: t('crm.productTypeBundle'),
  };
  const [activeTab, setActiveTab] = useState<'products' | 'packages'>('products');
  const [categories, setCategories] = useState<Category[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [showProductModal, setShowProductModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadProducts();
  }, [selectedCategory]);

  const loadData = async () => {
    try {
      setLoading(true);
      const cats = await pricelistService.getCategories();
      setCategories(cats);
      // Expandera alla kategorier som standard
      const allIds = new Set<string>();
      const collectIds = (cats: Category[]) => {
        cats.forEach(c => {
          allIds.add(c.id);
          if (c.children) collectIds(c.children);
        });
      };
      collectIds(cats);
      setExpandedCategories(allIds);
    } catch (error) {
      toast.error(t('crm.loadPricelistError'));
    } finally {
      setLoading(false);
    }
  };

  const loadProducts = async () => {
    try {
      const prods = await pricelistService.getProducts(selectedCategory || undefined);
      setProducts(prods);
    } catch {
      // Error loading products - handled silently
    }
  };

  const toggleCategory = (id: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSaveProduct = async (data: Partial<Product>) => {
    try {
      if (editingProduct) {
        await pricelistService.updateProduct(editingProduct.id, data);
        toast.success(t('crm.productUpdated'));
      } else {
        await pricelistService.createProduct(data);
        toast.success(t('crm.productCreated'));
      }
      setShowProductModal(false);
      setEditingProduct(null);
      loadProducts();
    } catch (error: any) {
      if (error?.response?.data?.code === 'DUPLICATE_CODE') {
        toast.error(t('crm.duplicateProductCode'));
      } else {
        toast.error(t('crm.saveProductError'));
      }
    }
  };

  const handleDeleteProduct = async (id: string) => {
    if (!confirm(t('crm.confirmDeleteProduct'))) return;
    try {
      await pricelistService.deleteProduct(id);
      toast.success(t('crm.productDeleted'));
      loadProducts();
    } catch (error) {
      toast.error(t('crm.deleteProductError'));
    }
  };

  const renderCategoryTree = (cats: Category[], level = 0) => {
    return cats.map(cat => (
      <div key={cat.id}>
        <div
          className={`flex items-center gap-2 py-2 px-3 cursor-pointer hover:bg-bgBase rounded transition-colors ${
            selectedCategory === cat.id ? 'bg-bgPrimary/10 text-bgPrimary' : ''
          }`}
          style={{ paddingLeft: `${12 + level * 16}px` }}
          onClick={() => setSelectedCategory(cat.id === selectedCategory ? null : cat.id)}
        >
          {cat.children && cat.children.length > 0 ? (
            <button onClick={(e) => { e.stopPropagation(); toggleCategory(cat.id); }}>
              {expandedCategories.has(cat.id) ? (
                <ChevronDownIcon className="w-4 h-4" />
              ) : (
                <ChevronRightIcon className="w-4 h-4" />
              )}
            </button>
          ) : (
            <span className="w-4" />
          )}
          <FolderIcon className="w-4 h-4 text-yellow-500" />
          <span className="flex-1 text-sm">{cat.name}</span>
          <span className="text-xs text-textSecondary">{productTypeLabels[cat.category_type]}</span>
        </div>
        {cat.children && expandedCategories.has(cat.id) && (
          <div>{renderCategoryTree(cat.children, level + 1)}</div>
        )}
      </div>
    ));
  };

  return (
    <div className="p-4 bg-bgContainer h-full flex flex-col">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate(ROUTES.CRM)} className="p-2 hover:bg-bgElevated rounded-lg transition-colors">
          <ArrowLeftIcon className="w-5 h-5 text-textDefault" />
        </button>
        <h1 className="text-2xl font-bold text-textDefault flex items-center gap-2">
          <CurrencyDollarIcon className="w-6 h-6 text-primary" />
          {t('crm.pricelist')}
        </h1>
      </div>
      {/* Flikar */}
      <div className="flex gap-1 mb-4 border-b border-border">
        <button
          onClick={() => setActiveTab('products')}
          className={`px-4 py-2 font-medium rounded-t-lg transition-colors ${
            activeTab === 'products'
              ? 'bg-bgPrimary text-white'
              : 'bg-bgElevated text-textSecondary hover:text-textDefault'
          }`}
        >
          {t('crm.tabProducts')}
        </button>
        <button
          onClick={() => setActiveTab('packages')}
          className={`px-4 py-2 font-medium rounded-t-lg transition-colors ${
            activeTab === 'packages'
              ? 'bg-bgPrimary text-white'
              : 'bg-bgElevated text-textSecondary hover:text-textDefault'
          }`}
        >
          {t('crm.tabPackages')}
        </button>
      </div>

      {activeTab === 'packages' ? (
        <SystemPackagesManager />
      ) : (
      <div className="flex-1 flex gap-4">
      {/* Vänster: Kategorier */}
      <div className="w-72 bg-bgElevated rounded-lg border border-border p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold text-textDefault">{t('crm.categoriesTitle')}</h2>
        </div>

        <button
          onClick={() => setSelectedCategory(null)}
          className={`w-full text-left py-2 px-3 rounded mb-2 ${
            selectedCategory === null ? 'bg-bgPrimary/10 text-bgPrimary' : 'hover:bg-bgBase'
          }`}
        >
          {t('crm.allProducts')}
        </button>

        {loading ? (
          <div className="text-center py-4 text-textSecondary">{t('common.loading')}</div>
        ) : (
          <div className="space-y-1">{renderCategoryTree(categories)}</div>
        )}
      </div>

      {/* Höger: Produkter */}
      <div className="flex-1 bg-bgElevated rounded-lg border border-border p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold text-textDefault">
            {selectedCategory ? categories.find(c => c.id === selectedCategory)?.name || t('crm.allProducts') : t('crm.allProducts')}
          </h2>
          <button
            onClick={() => { setEditingProduct(null); setShowProductModal(true); }}
            className="flex items-center gap-2 px-3 py-1.5 bg-bgPrimary text-white rounded hover:bg-bgPrimary/90"
          >
            <PlusIcon className="w-4 h-4" />
            {t('crm.newProduct')}
          </button>
        </div>

        {/* Produkttabell */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-2 px-3 text-textSecondary font-medium">{t('crm.colCode')}</th>
                <th className="text-left py-2 px-3 text-textSecondary font-medium">{t('crm.colName')}</th>
                <th className="text-left py-2 px-3 text-textSecondary font-medium">{t('crm.colType')}</th>
                <th className="text-left py-2 px-3 text-textSecondary font-medium">{t('crm.colPricingModel')}</th>
                <th className="text-right py-2 px-3 text-textSecondary font-medium">{t('crm.colPrice')}</th>
                <th className="text-left py-2 px-3 text-textSecondary font-medium">{t('crm.colUnit')}</th>
                <th className="w-20"></th>
              </tr>
            </thead>
            <tbody>
              {products.map(product => (
                <tr key={product.id} className="border-b border-border hover:bg-bgBase">
                  <td className="py-2 px-3 font-mono text-xs">{product.code}</td>
                  <td className="py-2 px-3">
                    <div className="font-medium text-textDefault">{product.name}</div>
                    {product.description && (
                      <div className="text-xs text-textSecondary truncate max-w-xs">{product.description}</div>
                    )}
                  </td>
                  <td className="py-2 px-3">
                    <span className={`px-2 py-0.5 text-xs rounded ${
                      product.product_type === 'service' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' :
                      product.product_type === 'material' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' :
                      'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400'
                    }`}>
                      {productTypeLabels[product.product_type]}
                    </span>
                  </td>
                  <td className="py-2 px-3">
                    <div className="flex items-center gap-1 text-textSecondary">
                      {pricingTypeLabels[product.pricing_type]?.icon}
                      <span className="text-xs">{pricingTypeLabels[product.pricing_type]?.label}</span>
                    </div>
                  </td>
                  <td className="py-2 px-3 text-right font-medium">
                    {product.price.toLocaleString('sv-SE')} {product.currency}
                  </td>
                  <td className="py-2 px-3 text-textSecondary">{product.unit}</td>
                  <td className="py-2 px-3">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => { setEditingProduct(product); setShowProductModal(true); }}
                        className="p-1 text-textSecondary hover:text-bgPrimary rounded"
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteProduct(product.id)}
                        className="p-1 text-textSecondary hover:text-red-500 rounded"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {products.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-textSecondary">
                    {t('crm.noProductsInCategory')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      </div>
      )}

      {/* Produktmodal */}
      {showProductModal && (
        <ProductModal
          product={editingProduct}
          categories={categories}
          onSave={handleSaveProduct}
          onClose={() => { setShowProductModal(false); setEditingProduct(null); }}
        />
      )}
    </div>
  );
};

// Produktmodal-komponent
const ProductModal: React.FC<{
  product: Product | null;
  categories: Category[];
  onSave: (data: Partial<Product>) => void;
  onClose: () => void;
}> = ({ product, categories, onSave, onClose }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<Partial<Product>>({
    code: product?.code || '',
    name: product?.name || '',
    description: product?.description || '',
    category_id: product?.category_id || null,
    product_type: product?.product_type || 'material',
    pricing_type: product?.pricing_type || 'monthly',
    price: product?.price || 0,
    unit: product?.unit || 'st',
  });

  const flattenCategories = (cats: Category[], level = 0): { cat: Category; level: number }[] => {
    let result: { cat: Category; level: number }[] = [];
    cats.forEach(cat => {
      result.push({ cat, level });
      if (cat.children) result = result.concat(flattenCategories(cat.children, level + 1));
    });
    return result;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-bgContainer rounded-lg p-6 w-full max-w-lg">
        <h2 className="text-xl font-bold text-textDefault mb-4">
          {product ? t('crm.editProduct') : t('crm.newProduct')}
        </h2>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-textSecondary mb-1">{t('crm.labelCode')}</label>
              <input
                type="text"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded bg-bgBase text-textDefault"
              />
            </div>
            <div>
              <label className="block text-sm text-textSecondary mb-1">{t('crm.labelCategory')}</label>
              <select
                value={formData.category_id || ''}
                onChange={(e) => setFormData({ ...formData, category_id: e.target.value || null })}
                className="w-full px-3 py-2 border border-border rounded bg-bgBase text-textDefault"
              >
                <option value="">{t('crm.noCategory')}</option>
                {flattenCategories(categories).map(({ cat, level }) => (
                  <option key={cat.id} value={cat.id}>
                    {'  '.repeat(level)}{cat.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm text-textSecondary mb-1">{t('crm.labelName')}</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-border rounded bg-bgBase text-textDefault"
              required
            />
          </div>

          <div>
            <label className="block text-sm text-textSecondary mb-1">{t('crm.labelDescription')}</label>
            <textarea
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border border-border rounded bg-bgBase text-textDefault"
              rows={2}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-textSecondary mb-1">{t('crm.labelProductType')}</label>
              <select
                value={formData.product_type}
                onChange={(e) => setFormData({ ...formData, product_type: e.target.value as any })}
                className="w-full px-3 py-2 border border-border rounded bg-bgBase text-textDefault"
              >
                <option value="service">{t('crm.productTypeService')}</option>
                <option value="material">{t('crm.productTypeMaterial')}</option>
                <option value="bundle">{t('crm.productTypeBundle')}</option>
              </select>
            </div>
            <div>
              <label className="block text-sm text-textSecondary mb-1">{t('crm.labelPricingModel')}</label>
              <select
                value={formData.pricing_type}
                onChange={(e) => setFormData({ ...formData, pricing_type: e.target.value as any })}
                className="w-full px-3 py-2 border border-border rounded bg-bgBase text-textDefault"
              >
                <option value="hourly">{t('crm.pricingHourly')}</option>
                <option value="monthly">{t('crm.pricingMonthly')}</option>
                <option value="per_vehicle">{t('crm.pricingPerVehicle')}</option>
                <option value="per_user">{t('crm.pricingPerUser')}</option>
                <option value="one_time">{t('crm.pricingOneTime')}</option>
                <option value="custom">{t('crm.pricingCustom')}</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-textSecondary mb-1">{t('crm.labelPrice')}</label>
              <input
                type="number"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-border rounded bg-bgBase text-textDefault"
                step="0.01"
              />
            </div>
            <div>
              <label className="block text-sm text-textSecondary mb-1">{t('crm.labelUnit')}</label>
              <input
                type="text"
                value={formData.unit}
                onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded bg-bgBase text-textDefault"
                placeholder={t('crm.unitPlaceholder')}
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-border rounded hover:bg-bgBase"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={() => onSave(formData)}
            className="px-4 py-2 bg-bgPrimary text-white rounded hover:bg-bgPrimary/90"
          >
            {t('common.save')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Pricelist;

